// src/pages/FaqAdmin.js - Optimized to reduce TinyMCE editor loads
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import '../css/FaqAdmin.css';
import api from '../components/api';

// Lazy load the TinyMCE editor
const LazyEditor = lazy(() => import('@tinymce/tinymce-react').then(module => ({ 
  default: module.Editor 
})));

const FaqAdmin = () => {
  const navigate = useNavigate();
  const [faqItems, setFaqItems] = useState([]);
  const [subjects, setSubjects] = useState({ nl: [], en: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // list, edit, create
  const [currentItem, setCurrentItem] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Editor references
  const editorNlRef = useRef(null);
  const editorEnRef = useRef(null);
  
  // Add new state variables for editor visibility
  const [showDutchEditor, setShowDutchEditor] = useState(false);
  const [showEnglishEditor, setShowEnglishEditor] = useState(false);
  
  // Add state to store editor content when not showing the actual editor
  const [dutchContent, setDutchContent] = useState('');
  const [englishContent, setEnglishContent] = useState('');
  
  // New item form state
  const [formData, setFormData] = useState({
    subject: '',
    subjectEn: '',
    newSubject: '',
    newSubjectEn: '',
    question: '',
    questionEn: '',
    answer: '',
    answerEn: '',
    active: true
  });

  // Clean up editors when component unmounts or view changes
  useEffect(() => {
    return () => {
      // Clean up editor references
      editorNlRef.current = null;
      editorEnRef.current = null;
    };
  }, []);

  // Reset editor states when switching views
  useEffect(() => {
    if (currentView === 'list') {
      setShowDutchEditor(false);
      setShowEnglishEditor(false);
      setDutchContent('');
      setEnglishContent('');
      editorNlRef.current = null;
      editorEnRef.current = null;
    }
  }, [currentView]);

  // When editing an existing item, set the content but don't show editors yet
  useEffect(() => {
    if (currentItem && currentView === 'edit') {
      setDutchContent(currentItem.answer || '');
      setEnglishContent(currentItem.answerEn || '');
    }
  }, [currentItem, currentView]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load FAQ items and subjects
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all FAQ items for admin view
        const response = await api.get('/api/faq/admin');
        setFaqItems(response.data);
        
        // Extract unique subjects for dropdowns
        const nlSubjects = [...new Set(response.data.map(item => item.subject).filter(Boolean))];
        const enSubjects = [...new Set(response.data.map(item => item.subjectEn).filter(Boolean))];
        
        setSubjects({
          nl: nlSubjects,
          en: enSubjects
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching FAQ data:', err);
        setError(err.message || 'Error fetching FAQ data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle auto-translate
  const handleAutoTranslate = async () => {
    try {
      setIsTranslating(true);
      
      // Get the Dutch content from the state or visible editor
      const dutchSubject = formData.subject === 'new' ? formData.newSubject : formData.subject;
      const dutchQuestion = formData.question;
      const dutchAnswer = showDutchEditor && editorNlRef.current 
        ? editorNlRef.current.getContent() 
        : dutchContent || formData.answer;
      
      if (!dutchSubject && !dutchQuestion && !dutchAnswer) {
        alert('Please fill in the Dutch content before translating.');
        setIsTranslating(false);
        return;
      }
      
      // Direct API endpoint
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      // Try to use real translations
      try {
        const response = await fetch(`${API_URL}/api/translate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            subject: dutchSubject,
            question: dutchQuestion,
            answer: dutchAnswer 
          })
        });
        
        if (response.ok) {
          const translations = await response.json();
          
          // Update form with translations
          if (formData.subjectEn === 'new' || formData.subjectEn === '') {
            setFormData(prev => ({
              ...prev,
              newSubjectEn: translations.subject || prev.newSubjectEn,
              questionEn: translations.question || prev.questionEn
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              questionEn: translations.question || prev.questionEn
            }));
          }
          
          // Update the English content in the state
          if (translations && translations.answer) {
            setEnglishContent(translations.answer);
            
            // If English editor is visible, update it directly
            if (showEnglishEditor && editorEnRef.current) {
              editorEnRef.current.setContent(translations.answer);
            }
          }
        } else {
          const errorText = await response.text();
          throw new Error(`Translation failed: ${errorText}`);
        }
      } catch (apiError) {
        // Only use alert for user-friendly messages
        console.error('Translation API error:', apiError);
        throw new Error('Could not connect to translation service. Please try again later.');
      }
    } catch (error) {
      console.error('Translation error:', error);
      alert(error.message);
    } finally {
      setIsTranslating(false);
    }
  };
  
  // Handle form submission for creating/editing FAQ item
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get editor content from state or editor if visible
      let answerNl = showDutchEditor && editorNlRef.current 
        ? editorNlRef.current.getContent() 
        : dutchContent || formData.answer;
        
      let answerEn = showEnglishEditor && editorEnRef.current 
        ? editorEnRef.current.getContent() 
        : englishContent || formData.answerEn;
      
      // Handle new subjects properly
      let finalSubject = formData.subject;
      let finalSubjectEn = formData.subjectEn;
      
      if (formData.subject === 'new' && formData.newSubject) {
        finalSubject = formData.newSubject;
      }
      
      if (formData.subjectEn === 'new' && formData.newSubjectEn) {
        finalSubjectEn = formData.newSubjectEn;
      }
      
      const submitData = {
        ...formData,
        subject: finalSubject,
        subjectEn: finalSubjectEn,
        answer: answerNl,
        answerEn: answerEn
      };
      
      if (currentView === 'create') {
        // Create new item
        const response = await api.post('/api/faq', submitData);
        
        // Add to local state
        setFaqItems([...faqItems, response.data.item]);
        
        // Reset form and go back to list view
        setFormData({
          subject: '',
          subjectEn: '',
          newSubject: '',
          newSubjectEn: '',
          question: '',
          questionEn: '',
          answer: '',
          answerEn: '',
          active: true
        });
        
        // Reset editor states
        setShowDutchEditor(false);
        setShowEnglishEditor(false);
        setDutchContent('');
        setEnglishContent('');
        
        setCurrentView('list');
        
        // Success message
        alert('FAQ item created successfully!');
      } else if (currentView === 'edit' && currentItem) {
        // Update existing item
        const response = await api.put(`/api/faq/${currentItem._id}`, submitData);
        
        // Update in local state
        setFaqItems(faqItems.map(item => 
          item._id === currentItem._id ? response.data.item : item
        ));
        
        // Reset editor states
        setShowDutchEditor(false);
        setShowEnglishEditor(false);
        setDutchContent('');
        setEnglishContent('');
        
        // Go back to list view
        setCurrentView('list');
        setCurrentItem(null);
        
        // Success message
        alert('FAQ item updated successfully!');
      }
    } catch (err) {
      console.error('Error saving FAQ item:', err);
      setError(err.message || 'Error saving FAQ item');
      alert('Error saving FAQ item: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Handle edit button click
  const handleEdit = (item) => {
    setCurrentItem(item);
    setFormData({
      subject: item.subject || '',
      subjectEn: item.subjectEn || '',
      newSubject: '',
      newSubjectEn: '',
      question: item.question || '',
      questionEn: item.questionEn || '',
      answer: item.answer || '',
      answerEn: item.answerEn || '',
      active: item.active !== false
    });
    
    // Set the content in our state variables, but don't show editors yet
    setDutchContent(item.answer || '');
    setEnglishContent(item.answerEn || '');
    
    setCurrentView('edit');
    
    // Scroll to top when editing
    window.scrollTo(0, 0);
  };
  
  // Handle delete button click
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ item?')) {
      return;
    }
    
    try {
      await api.delete(`/api/faq/${id}`);
      
      // Remove from local state
      setFaqItems(faqItems.filter(item => item._id !== id));
      
      // Success message
      alert('FAQ item deleted successfully!');
    } catch (err) {
      console.error('Error deleting FAQ item:', err);
      setError(err.message || 'Error deleting FAQ item');
      alert('Error deleting FAQ item: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Handle import from Google Sheets
  const handleImport = async () => {
    if (!window.confirm('This will import FAQ items from Google Sheets. Continue?')) {
      return;
    }
    
    try {
      setImportStatus('Importing FAQ items from Google Sheets...');
      
      const response = await api.get('/api/faq/import-from-sheets');
      
      setImportStatus(`Import completed: ${response.data.results.imported} new, ${response.data.results.updated} updated, ${response.data.results.failed} failed`);
      
      // Refresh the list
      const refreshResponse = await api.get('/api/faq/admin');
      setFaqItems(refreshResponse.data);
      
      // Extract unique subjects for dropdowns from updated list
      const nlSubjects = [...new Set(refreshResponse.data.map(item => item.subject).filter(Boolean))];
      const enSubjects = [...new Set(refreshResponse.data.map(item => item.subjectEn).filter(Boolean))];
      
      setSubjects({
        nl: nlSubjects,
        en: enSubjects
      });
      
      // Success message
      setTimeout(() => {
        alert('Import completed successfully!');
      }, 500);
    } catch (err) {
      console.error('Error importing from Google Sheets:', err);
      setImportStatus(`Error: ${err.message || 'Failed to import'}`);
      alert('Error importing from Google Sheets: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Filter FAQ items by search term
  const filteredFaqItems = searchTerm 
    ? faqItems.filter(item => 
        (item.question && item.question.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.subject && item.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.questionEn && item.questionEn.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.subjectEn && item.subjectEn.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : faqItems;
  
  // Group FAQ items by subject
  const groupedFaqItems = filteredFaqItems.reduce((acc, item) => {
    const subject = item.subject || 'Uncategorized';
    if (!acc[subject]) {
      acc[subject] = [];
    }
    acc[subject].push(item);
    return acc;
  }, {});
  
  // Render FAQ items list
  const renderFaqList = () => {
    if (filteredFaqItems.length === 0) {
      return (
        <div className="no-items">
          {searchTerm ? 'No FAQ items match your search.' : 'No FAQ items found.'}
        </div>
      );
    }
    
    return Object.entries(groupedFaqItems).map(([subject, items]) => (
      <div key={subject} className="faq-admin-subject">
        <h3>{subject}</h3>
        <div className="table-responsive">
          <table className="faq-admin-table">
            <thead>
              <tr>
                <th>Question (NL)</th>
                <th>Question (EN)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item._id} className={!item.active ? 'inactive-item' : ''}>
                  <td>{item.question}</td>
                  <td>
                    {item.questionEn || (
                      <span className="missing-translation">No translation</span>
                    )}
                  </td>
                  <td>{item.active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-button" 
                        onClick={() => handleEdit(item)}
                        aria-label={`Edit question: ${item.question}`}
                      >
                        Edit
                      </button>
                      
                      <button 
                        className="delete-button" 
                        onClick={() => handleDelete(item._id)}
                        aria-label={`Delete question: ${item.question}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ));
  };
  
  // Render form for editing/creating FAQ item
  const renderForm = () => {
    return (
      <form onSubmit={handleSubmit} className="faq-admin-form">
        <h2>{currentView === 'create' ? 'Create New FAQ Item' : 'Edit FAQ Item'}</h2>
        
        {/* Subject selection */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="subject">Subject (NL) *</label>
            <select 
              id="subject" 
              name="subject" 
              value={formData.subject} 
              onChange={handleInputChange}
              required
            >
              <option value="">Select a subject...</option>
              {subjects.nl.map((subject, index) => (
                <option key={index} value={subject}>{subject}</option>
              ))}
              <option value="new">+ Add new subject</option>
            </select>
            
            {formData.subject === 'new' && (
              <input
                type="text"
                name="newSubject"
                placeholder="Enter new subject"
                value={formData.newSubject || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    newSubject: e.target.value
                  });
                }}
                required
                style={{ marginTop: '8px' }}
              />
            )}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="subjectEn">Subject (EN)</label>
            <select 
              id="subjectEn" 
              name="subjectEn" 
              value={formData.subjectEn} 
              onChange={handleInputChange}
            >
              <option value="">Select a subject...</option>
              {subjects.en.map((subject, index) => (
                <option key={index} value={subject}>{subject}</option>
              ))}
              <option value="new">+ Add new subject</option>
            </select>
            
            {formData.subjectEn === 'new' && (
              <input
                type="text"
                name="newSubjectEn"
                placeholder="Enter new subject (EN)"
                value={formData.newSubjectEn || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    newSubjectEn: e.target.value
                  });
                }}
                style={{ marginTop: '8px' }}
              />
            )}
          </div>
        </div>
        
        {/* Questions */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="question">Question (NL) *</label>
            <textarea
              id="question"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              required
              rows="3"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="questionEn">Question (EN)</label>
            <textarea
              id="questionEn"
              name="questionEn"
              value={formData.questionEn}
              onChange={handleInputChange}
              rows="3"
            />
          </div>
        </div>
        
        {/* Translation button */}
        <div className="translation-buttons">
          <button 
            type="button" 
            className="translate-button"
            onClick={handleAutoTranslate}
            disabled={isTranslating}
          >
            {isTranslating ? 'Translating...' : 'Auto-translate to English'}
          </button>
          <span className="translation-note">
            This will translate the Dutch content to English using Google Translate
          </span>
        </div>
        
    {/* Custom button helper for inserting contact form triggers */}
<div className="button-helper">
  <button 
    type="button" 
    className="insert-button-helper"
    onClick={() => {
      if (showDutchEditor && editorNlRef.current) {
        // Get the current editor instance
        const editor = editorNlRef.current;
        
        // Prompt for button text
        const buttonText = prompt('Voer de knoptekst in:', 'Stel een vraag aan het bestuur');
        if (!buttonText) return;
        
        // Insert the contact form trigger button (no URL needed)
        const buttonHtml = `<a href="#contact" class="faq-button contact-form-trigger">${buttonText}</a>`;
        editor.insertContent(buttonHtml);
        
        // Show instruction
        alert('Contact formulier knop toegevoegd! Deze zal automatisch het ingebouwde contactformulier openen op de website.');
      } else {
        // Editor not visible - show message to open editor first
        alert('Open eerst de Rich Editor om een contact knop in te voegen.');
        setShowDutchEditor(true);
      }
    }}
  >
    Voeg Contact Knop Toe (Nederlands)
  </button>
  
  <button 
    type="button" 
    className="insert-button-helper"
    onClick={() => {
      if (showEnglishEditor && editorEnRef.current) {
        // Get the current editor instance for English
        const editor = editorEnRef.current;
        
        // Prompt for button text
        const buttonText = prompt('Enter the button text:', 'Contact the board');
        if (!buttonText) return;
        
        // Insert the contact form trigger button (no URL needed)
        const buttonHtml = `<a href="#contact" class="faq-button contact-form-trigger">${buttonText}</a>`;
        editor.insertContent(buttonHtml);
        
        // Show instruction
        alert('Contact form button added! This will automatically open the built-in contact form on the website.');
      } else {
        // Editor not visible - show message to open editor first
        alert('Please open the Rich Editor first to insert a contact button.');
        setShowEnglishEditor(true);
      }
    }}
  >
    Add Contact Button (English)
  </button>
  
  {/* Legacy form button helpers - kept for backward compatibility */}
  <button 
    type="button" 
    className="insert-button-helper"
    onClick={() => {
      if (showDutchEditor && editorNlRef.current) {
        const editor = editorNlRef.current;
        const url = prompt('Voer de externe formulier URL in:', 'https://forms.gle/');
        if (!url) return;
        
        const buttonText = prompt('Voer de knoptekst in:', 'Naar het externe formulier');
        if (!buttonText) return;
        
        editor.insertContent(`<a href="${url}" class="faq-button" target="_blank" rel="noopener noreferrer">${buttonText}</a>`);
      } else {
        alert('Open eerst de Rich Editor om een externe formulier knop in te voegen.');
        setShowDutchEditor(true);
      }
    }}
  >
    Externe Formulier Knop (Nederlands)
  </button>
  
  <button 
    type="button" 
    className="insert-button-helper"
    onClick={() => {
      if (showEnglishEditor && editorEnRef.current) {
        const editor = editorEnRef.current;
        const url = prompt('Enter the external form URL:', 'https://forms.gle/');
        if (!url) return;
        
        const buttonText = prompt('Enter the button text:', 'Go to external form');
        if (!buttonText) return;
        
        editor.insertContent(`<a href="${url}" class="faq-button" target="_blank" rel="noopener noreferrer">${buttonText}</a>`);
      } else {
        alert('Please open the Rich Editor first to insert an external form button.');
        setShowEnglishEditor(true);
      }
    }}
  >
    External Form Button (English)
  </button>
  
  <div className="helper-info">
    <p><strong>Contact Knoppen:</strong> Gebruiken het ingebouwde contactformulier - geen externe URL nodig.</p>
    <p><strong>Externe Formulier Knoppen:</strong> Voor Google Forms of andere externe formulieren.</p>
    <p>Alle knoppen worden automatisch gestyleerd op de FAQ pagina.</p>
  </div>
</div>
        
        {/* Dutch Answer Field with Lazy Loading */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="answer">Answer (NL) *</label>
            
            {!showDutchEditor ? (
              <div className="editor-placeholder">
                <textarea
                  value={dutchContent || formData.answer || ''}
                  onChange={(e) => setDutchContent(e.target.value)}
                  rows="6"
                  placeholder="Enter answer in Dutch or click 'Open Rich Editor' to use the formatting tools"
                  style={{ width: '100%', padding: '10px' }}
                />
                <button 
                  type="button" 
                  className="open-editor-button"
                  onClick={() => setShowDutchEditor(true)}
                >
                  Open Rich Editor
                </button>
              </div>
            ) : (
              <Suspense fallback={<div>Loading editor...</div>}>
                <LazyEditor
                  apiKey="8czevb54iigf73fsgvpmro69urh483qlnlqmot23u4p1c3fn"
                  onInit={(evt, editor) => editorNlRef.current = editor}
                  initialValue={dutchContent || formData.answer || ''}
                  init={{
                    height: isMobile ? 250 : 300,
                    menubar: !isMobile,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'paste', 'code', 'help', 'wordcount'
                    ],
                    toolbar: isMobile 
                      ? 'bold italic | link | bullist numlist | insertformbutton' 
                      : 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | insertformbutton | removeformat | help',
                    content_style: `
                      body { 
                        font-family: Helvetica, Arial, sans-serif; 
                        font-size: 14px;
                      }
                      .faq-button {
                        display: inline-block;
                        padding: 12px 20px;
                        background-color: #246834;
                        color: white;
                        border-radius: 8px;
                        text-decoration: none;
                        font-weight: 600;
                        margin: 15px 0;
                        cursor: pointer;
                        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
                      }
                      .faq-button:hover {
                        background-color: #1a502a;
                      }
                    `,
                    mobile: {
                      menubar: false,
                      toolbar_mode: 'sliding',
                      toolbar: 'bold italic | link | bullist numlist | insertformbutton'
                    },
                    setup: function(editor) {
                      editor.ui.registry.addButton('insertformbutton', {
                        text: 'Form Button',
                        tooltip: 'Insert a form button',
                        onAction: function() {
                          const url = prompt('Enter the form or link URL:', 'https://forms.gle/');
                          if (!url) return;
                          
                          const buttonText = prompt('Enter the button text:', 'Naar het formulier');
                          if (!buttonText) return;
                          
                          editor.insertContent(`<a href="${url}" class="faq-button" target="_blank" rel="noopener noreferrer">${buttonText}</a>`);
                        }
                      });
                    }
                  }}
                  onBlur={(e, editor) => {
                    // Save content on blur for when we hide the editor
                    setDutchContent(editor.getContent());
                  }}
                />
                <button 
                  type="button" 
                  className="close-editor-button"
                  onClick={() => {
                    // Save content before closing
                    if (editorNlRef.current) {
                      setDutchContent(editorNlRef.current.getContent());
                    }
                    setShowDutchEditor(false);
                  }}
                >
                  Close Rich Editor
                </button>
              </Suspense>
            )}
          </div>
        </div>
        
        {/* English Answer Field with Lazy Loading */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="answerEn">Answer (EN)</label>
            
            {!showEnglishEditor ? (
              <div className="editor-placeholder">
                <textarea
                  value={englishContent || formData.answerEn || ''}
                  onChange={(e) => setEnglishContent(e.target.value)}
                  rows="6"
                  placeholder="Enter answer in English or click 'Open Rich Editor' to use the formatting tools"
                  style={{ width: '100%', padding: '10px' }}
                />
                <button 
                  type="button" 
                  className="open-editor-button"
                  onClick={() => setShowEnglishEditor(true)}
                >
                  Open Rich Editor
                </button>
              </div>
            ) : (
              <Suspense fallback={<div>Loading editor...</div>}>
                <LazyEditor
                  apiKey="8czevb54iigf73fsgvpmro69urh483qlnlqmot23u4p1c3fn"
                  onInit={(evt, editor) => editorEnRef.current = editor}
                  initialValue={englishContent || formData.answerEn || ''}
                  init={{
                    height: isMobile ? 250 : 300,
                    menubar: !isMobile,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'paste', 'code', 'help', 'wordcount'
                    ],
                    toolbar: isMobile 
                      ? 'bold italic | link | bullist numlist | insertformbutton' 
                      : 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | insertformbutton | removeformat | help',
                    content_style: `
                      body { 
                        font-family: Helvetica, Arial, sans-serif; 
                        font-size: 14px;
                      }
                      .faq-button {
                        display: inline-block;
                        padding: 12px 20px;
                        background-color: #246834;
                        color: white;
                        border-radius: 8px;
                        text-decoration: none;
                        font-weight: 600;
                        margin: 15px 0;
                        cursor: pointer;
                        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
                      }
                      .faq-button:hover {
                        background-color: #1a502a;
                      }
                    `,
                    mobile: {
                      menubar: false,
                      toolbar_mode: 'sliding',
                      toolbar: 'bold italic | link | bullist numlist | insertformbutton'
                    },
                    setup: function(editor) {
                      editor.ui.registry.addButton('insertformbutton', {
                        text: 'Form Button',
                        tooltip: 'Insert a form button',
                        onAction: function() {
                          const url = prompt('Enter the form or link URL:', 'https://forms.gle/');
                          if (!url) return;
                          
                          const buttonText = prompt('Enter the button text:', 'Fill out the form');
                          if (!buttonText) return;
                          
                          editor.insertContent(`<a href="${url}" class="faq-button" target="_blank" rel="noopener noreferrer">${buttonText}</a>`);
                        }
                      });
                    }
                  }}
                  onBlur={(e, editor) => {
                    // Save content on blur for when we hide the editor
                    setEnglishContent(editor.getContent());
                  }}
                />
                <button 
                  type="button" 
                  className="close-editor-button"
                  onClick={() => {
                    // Save content before closing
                    if (editorEnRef.current) {
                      setEnglishContent(editorEnRef.current.getContent());
                    }
                    setShowEnglishEditor(false);
                  }}
                >
                  Close Rich Editor
                </button>
              </Suspense>
            )}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
                id="active-checkbox"
              />
              Active
            </label>
            <div className="status-help">
              When active, this FAQ item will be visible on the public FAQ page
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button type="submit" className="save-button" onClick={() => {
            // Ensure we have the latest content before submitting
            if (editorNlRef.current && showDutchEditor) {
              setDutchContent(editorNlRef.current.getContent());
            }
            if (editorEnRef.current && showEnglishEditor) {
              setEnglishContent(editorEnRef.current.getContent());
            }
          }}>
            {currentView === 'create' ? 'Create FAQ Item' : 'Update FAQ Item'}
          </button>
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => {
              setCurrentView('list');
              setCurrentItem(null);
              // Reset editor states
              setShowDutchEditor(false);
              setShowEnglishEditor(false);
              setDutchContent('');
              setEnglishContent('');
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };
  
  // Main render
  return (
    <div className="beheer-wrapper">
      <BeheerdersportaalSidebar>
        <main className="dashboard">
          <h1>FAQ Management</h1>
          
          {error && <div className="error-message">{error}</div>}
          {importStatus && <div className="import-status">{importStatus}</div>}
          
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading FAQ data...</p>
            </div>
          ) : (
            <>
              {currentView === 'list' && (
                <>
                  <div className="faq-admin-actions">
                    <button 
                      className="create-button"
                      onClick={() => {
                        setCurrentView('create');
                        setCurrentItem(null);
                        setFormData({
                          subject: '',
                          subjectEn: '',
                          newSubject: '',
                          newSubjectEn: '',
                          question: '',
                          questionEn: '',
                          answer: '',
                          answerEn: '',
                          active: true
                        });
                        // Reset editor states
                        setShowDutchEditor(false);
                        setShowEnglishEditor(false);
                        setDutchContent('');
                        setEnglishContent('');
                      }}
                    >
                      Create New FAQ Item
                    </button>
                    {/* <button className="import-button" onClick={handleImport}>
                      Import from Google Sheets
                    </button> */}
                  </div>
                  
                  <div className="search-container">
                    <input
                      type="text"
                      placeholder="Search FAQ items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </>
              )}
              
              {currentView === 'list' ? renderFaqList() : renderForm()}
            </>
          )}
        </main>
      </BeheerdersportaalSidebar>
    </div>
  );
};

export default FaqAdmin;